import React, { useState, useEffect, useCallback, useMemo } from "react";
import LeftSidebar from "./LeftSidebar";
import CenterPanel from "./CenterPanel";
import RightSidebar from "./RightSidebar";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db, auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../hooks/notificationsContext";
import getServerOffset from "../utils/getServerOffset";
import { useGoldMinter } from "../hooks/useGoldMinter";
import usePortfolioUpdater from '../hooks/usePortfolioUpdater';
import GameTutorial from "./GameTutorial";

export default function TradingDashboard({ onLogout, calculatePortfolio }) {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [userData, setUserData] = useState(null);
  const [windowState, setWindowState] = useState("Trading");
  const [sidebarPortfolioTab, setSidebarPortfolioTab] = useState("Portfolio");
  const [companies, setCompanies] = useState([]);
  const [showDisplayNamePrompt, setShowDisplayNamePrompt] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [creatingPlayer, setCreatingPlayer] = useState(false);
  const [loadingUserDoc, setLoadingUserDoc] = useState(true);
  const { triggerManually } = usePortfolioUpdater({ triggerOnLogin: true });
  const navigate = useNavigate();
  const cooldownRef = React.useRef(false);
  const { messages, addNotification } = useNotifications();
  const currentUser = auth.currentUser;
  const [isTutorialActive, setIsTutorialActive] = useState(true);
  const playerReady = !!userData?.uid && !!userData?.userDisplayName;
  const [isUserLoading, setIsUserLoading] = useState(true);

const [activeVote, setActiveVote] = useState(null);

useEffect(() => {
  const voteRef = doc(db, "Votes", "current_resolution");

  const unsubscribe = onSnapshot(voteRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.status === "active") {
        setActiveVote({ id: docSnap.id, ...data });
      } else {
        setActiveVote(null);
      }
    } else {
      setActiveVote(null);
    }
  });

  return () => unsubscribe();
}, []);


  useGoldMinter();

  const handleSelectedPlayer = useCallback((playerName) => {
    setSelectedPlayer(playerName);
    setWindowState("PlayerProfile");
    setSidebarPortfolioTab("Profile");
  }, []);

  const handleCloseCompany = useCallback(() => setSelectedCompany(null), []);
  const handleClosePlayer = useCallback(() => setSelectedPlayer(null), []);

  const stableUserForChat = useMemo(() => {
    if (!userData) return null;
    return {
      uid: userData.uid,
      userCompanyTicker: userData.userCompanyTicker,
      userLevel: userData.userLevel,
      userRegion: userData.userRegion,
    };
  }, [userData]);

  const runPortfolioCalculation = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(
        "https://calculateportfolio-5dhfwyjvzq-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ uid: currentUser.uid }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error("Portfolio calculation failed:", err);
    }
  }, [currentUser]);

  const schedulePortfolioCalculation = useCallback(() => {
    if (cooldownRef.current) return;
    cooldownRef.current = true;
    runPortfolioCalculation();
    setTimeout(() => {
      cooldownRef.current = false;
    }, 10000);
  }, [runPortfolioCalculation]);

  const handleTransactionComplete = useCallback(() => {
    schedulePortfolioCalculation();
  }, [schedulePortfolioCalculation]);

  const handleCreateInitialPlayer = async (proposedDisplayName) => {
    if (creatingPlayer || !proposedDisplayName) return;
    setCreatingPlayer(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not authenticated");

      const idToken = await user.getIdToken();
      const res = await fetch(
        "https://createinitialplayer-5dhfwyjvzq-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ proposedDisplayName }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      setShowDisplayNamePrompt(false);

    } catch (err) {
      console.error(err);
      addNotification(`Failed to create player: ${err.message}`, "error");
    } finally {
      setCreatingPlayer(false);
    }
  };

  useEffect(() => {
    if (!currentUser?.uid) return;

    const userRef = doc(db, "Users", currentUser.uid);

    const fetchUser = async () => {
      setLoadingUserDoc(true);
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        setUserData(null);
        setShowDisplayNamePrompt(true);
      } else {
        setUserData(docSnap.data());
      }

      setLoadingUserDoc(false);

      return onSnapshot(userRef, (snap) => {
        if (snap.exists()) setUserData(snap.data());
      });
    };

    const unsub = fetchUser();

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [currentUser]);

useEffect(() => {
  if (!currentUser?.uid || !userData?.userDisplayName) return;

  const unsub = onSnapshot(doc(db, "Global_Vars", "Banned_Players"), (snap) => {
    if (!snap.exists()) return;
    const data = snap.data();

    const banReason = data[userData.userDisplayName];
    if (banReason) {
      localStorage.setItem("banReason", banReason);
      addNotification(`You have been banned: ${banReason}`, "error", new Date());
      navigate("/banned");
    }
  });

  return () => unsub();
}, [currentUser, userData?.userDisplayName, navigate, addNotification]);

useEffect(() => {
  if (!userData || Object.keys(userData).length === 0) {
    setIsUserLoading(true);
    return;
  }

  const isComplete = userData.userTutorial === true || userData.userTutorial === "true";
  
  if (!isComplete) {
    setIsTutorialActive(true);
  } else {
    setIsTutorialActive(false);
  }

  setIsUserLoading(false);
}, [userData]);

useEffect(() => {
  const fetchMOTD = async () => {
    try {
      const offset = await getServerOffset();
      const serverTime = Date.now() + offset;
      const motdDoc = await getDoc(doc(db, "Global_Vars", "Main"));
      if (motdDoc.exists()) {
        let message = motdDoc.data().globalMOTD;
        if (message.includes("{time}")) {
          const d = new Date(serverTime);
          const hh = String(d.getHours()).padStart(2, "0");
          const mm = String(d.getMinutes()).padStart(2, "0");
          const ss = String(d.getSeconds()).padStart(2, "0");
          message = message.replace("{time}", `${hh}:${mm}:${ss}`);
        }
        addNotification(message, "info", serverTime);
      }
    } catch (err) {
      addNotification(`Failed to load MOTD: ${err.message}`, "error");
    }
  };

  fetchMOTD();
}, [addNotification]);

useEffect(() => {
  if (!playerReady) return;

  schedulePortfolioCalculation();
  const intervalId = setInterval(schedulePortfolioCalculation, 5 * 60 * 1000);
  return () => clearInterval(intervalId);
}, [playerReady,schedulePortfolioCalculation]);

  if (loadingUserDoc && !showDisplayNamePrompt) {
    return <div>Loading player data...</div>;
  }

return (
  <div 
    id="v2-viewport-root" 
    style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh', 
      overflow: 'hidden', 
      background: '#0d1615',
      display: 'flex',
      flexDirection: 'column'
    }}
  >
    <div 
      id="v2-app-container" 
      style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        flex: 1, 
        width: '100%', 
        overflow: 'hidden',
        alignItems: 'stretch'
      }}
    >
      <GameTutorial 
        isActive={isTutorialActive} 
        isUserLoading={isUserLoading}
        isTutorialActive={isTutorialActive}
        onComplete={() => setIsTutorialActive(false)} 
      />

      {/* 1. LEFT SIDEBAR */}
      <div style={{ flexShrink: 0, height: '100%', display: 'flex' }}>
        <LeftSidebar
          onLogout={onLogout}
          setWindowState={setWindowState}
          windowState={windowState}
          selectedCompany={selectedCompany}
          setSelectedCompany={setSelectedCompany}
          selectedPlayer={selectedPlayer}
          setSelectedPlayer={setSelectedPlayer}
          onClose={handleClosePlayer}
          setPortfolioTab={setSidebarPortfolioTab}
          sidebarPortfolioTab={sidebarPortfolioTab}
          setUserData={setUserData}
          userData={userData}
          companies={companies}
          currentUser={currentUser}
          activeVote={activeVote}
          addNotification={addNotification}
          calculatePortfolio={calculatePortfolio}
        />
      </div>

      {/* 2. CENTER PANEL */}
      <main 
        style={{ 
          flex: 1, 
          height: '100%', 
          minWidth: 0, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <CenterPanel
          selectedCompany={selectedCompany}
          setSelectedCompany={setSelectedCompany}
          userData={userData}
          windowState={windowState}
          messages={messages}
          selectedPlayer={selectedPlayer}
          onTransactionComplete={handleTransactionComplete}
          setCompanies={setCompanies}
        />
      </main>

{/* 3. RIGHT SIDEBAR */}
<div 
  className="v2-sidebar-right-fixed" 
  style={{ 
    width: '550px', 
    minWidth: '500px', 
    height: '100%', 
    flexShrink: 0, 
    borderLeft: '1px solid rgba(80, 227, 194, 0.2)',
    background: '#0d1615',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', 
    position: 'relative'
  }}
>
  <RightSidebar
    selectedCompany={selectedCompany}
    setSelectedCompany={setSelectedCompany}
    onClose={handleCloseCompany}
    messages={messages}
    activeTab={windowState}
    setWindowState={setWindowState}
    addNotification={addNotification}
    onSelectedPlayer={handleSelectedPlayer}
    setSelectedPlayer={setSelectedPlayer}
    userData={stableUserForChat}
    onTransactionSuccess={triggerManually}
  />
</div>
    </div>

    {/* MODAL OVERLAY */}
{showDisplayNamePrompt && (
  <div style={{ 
    zIndex: 9999, 
    position: 'fixed', 
    inset: 0, 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    background: 'rgba(3, 10, 10, 0.95)',
    backdropFilter: 'blur(4px)'
  }}>
    <div style={{ 
      background: '#061616', 
      padding: '2px',
      border: '1px solid #50e3c2', 
      boxShadow: '0 0 20px rgba(80, 227, 194, 0.15)',
      width: '340px',
      fontFamily: "'JetBrains Mono', monospace"
    }}>
      {/* Header Bar */}
      <div style={{ 
        background: '#50e3c2', 
        color: '#061616', 
        padding: '4px 12px', 
        fontSize: '0.65rem', 
        fontWeight: 'bold', 
        textTransform: 'uppercase',
        letterSpacing: '1px',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <span>Trader Creation</span>
      </div>

      <div style={{ padding: '20px' }}>
        <div style={{ 
          color: '#50e3c2', 
          marginBottom: '16px', 
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          ENTER DISPLAY NAME
        </div>
        
<input
  type="text"
  placeholder="USERNAME"
  value={displayName}
  onChange={(e) => setDisplayName(e.target.value)} 
  onKeyDown={(e) => {
    if (e.key === 'Enter' && displayName.trim() && !creatingPlayer) {
      handleCreateInitialPlayer(displayName);
    }
  }}
  style={{ 
    width: '100%', 
    background: 'rgba(0,0,0,0.3)', 
    border: '1px solid rgba(80, 227, 194, 0.3)', 
    color: '#50e3c2', 
    padding: '12px', 
    marginBottom: '20px',
    fontSize: '0.9rem',
    outline: 'none',
    fontFamily: "'JetBrains Mono', monospace",
    boxSizing: 'border-box'
  }}
/>

        <button 
          onClick={() => handleCreateInitialPlayer(displayName)}
          disabled={creatingPlayer || !displayName.trim()}
          className="jetbrains-btn"
          style={{ 
            width: '100%', 
            background: 'transparent', 
            color: '#50e3c2', 
            border: '1px solid #50e3c2', 
            padding: '12px', 
            fontWeight: 'bold', 
            cursor: 'pointer',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '2px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(80, 227, 194, 0.1)';
            e.target.style.boxShadow = '0 0 15px rgba(80, 227, 194, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.boxShadow = 'none';
          }}
        >
          {creatingPlayer ? "LOADING..." : "CONFIRM"}
        </button>
        
      </div>
    </div>
  </div>
)}
  </div>
);
}
